
import { trpc } from '@tencent/tpro_rmonitor_metrics_server_get_metrics';
import * as uuid from 'uuid';
import AbstractHttpClient from '@/infrastructure/axios/request';
import { logger } from '@/infrastructure/logger';
import { getName } from '@/presentation/pages/overview/quality-report/config';
import { Dashboard } from '@/proto/dashboard';
import {
  IGetDashboardDataReq,
  IGetDashboardDataRsp,
  IGetDimensionDataReq,
  IGetDimensionDataRsp,
  IGetTopDataReq,
  IGetTopDataRsp,
} from '@/proto/metric';
import { MetricType as QualityReportMetricType } from '@/service/quality-report';
import { IMetricData } from '@/service/rum';
import { TrpcService } from '@/service/trpc';
import { fromPBTime, NoUndefinedField, WithTypeCast } from '@raft/utils';

export type IGetMetricsReq = WithTypeCast<trpc.rmonitor.metrics_server.IGetMetricsReq>;
export type IGetMetricsRsp = WithTypeCast<trpc.rmonitor.metrics_server.IGetMetricsRsp>;
export type IDimensionData = WithTypeCast<trpc.rmonitor.metrics_server.IDimensionData>;
export type IGetQualityReportReq = WithTypeCast<trpc.rmonitor.metrics_server.IGetQualityReportReq>;
export type IGetQualityReportRsp = WithTypeCast<trpc.rmonitor.metrics_server.IGetQualityReportRsp>;
export type IGetQualityReportTrendReq = WithTypeCast<trpc.rmonitor.metrics_server.IGetQualityReportTrendReq>;
export type IGetQualityReportTrendRsp = WithTypeCast<trpc.rmonitor.metrics_server.IGetQualityReportTrendRsp>;

export type IGetSummaryReq = WithTypeCast<trpc.rmonitor.metrics_server.IGetSummaryReq>;
export type IGetSummaryRsp = WithTypeCast<trpc.rmonitor.metrics_server.IGetSummaryRsp>;

export type IGetSummaryDataReq = WithTypeCast<trpc.rmonitor.metrics_server.IGetSummaryDataReq>;
export type IGetSummaryDataRsp = WithTypeCast<trpc.rmonitor.metrics_server.IGetSummaryDataRsp>;

export type ISummaryData = WithTypeCast<trpc.rmonitor.metrics_server.ISummaryData>;

export type IGetChartDashboardDataReq = WithTypeCast<trpc.rmonitor.metrics_server.IGetChartDashboardDataReq>;
export type IGetChartDashboardDataRsp = WithTypeCast<trpc.rmonitor.metrics_server.IGetChartDashboardDataRsp>;
export type ITimeTrendData = WithTypeCast<trpc.rmonitor.metrics_server.ITimeTrendData>;
export type IGroupedTimeSeries = WithTypeCast<trpc.rmonitor.metrics_server.IGroupedTimeSeries>;

export type IMetricQuality = WithTypeCast<trpc.rmonitor.metrics_server.IMetricQuality>;
export type MetricType = trpc.rmonitor.metrics_server.MetricType;
export type MetricName = trpc.rmonitor.metrics_server.MetricName;
export const { MetricType, MetricName } = trpc.rmonitor.metrics_server;

export type IGetStandardSummaryReq = WithTypeCast<trpc.rmonitor.metrics_server.IGetStandardSummaryReq>;
export type IGetStandardSummaryRsp = WithTypeCast<trpc.rmonitor.metrics_server.IGetStandardSummaryRsp>;
export type ISummaryGroupItem = WithTypeCast<trpc.rmonitor.metrics_server.ISummaryGroupItem>;

export type IGetDimensionListReq = WithTypeCast<trpc.rmonitor.metrics_server.IGetDimensionListReq>;
export type IGetDimensionListRsp = WithTypeCast<trpc.rmonitor.metrics_server.IGetDimensionListRsp>;

export interface IMetric extends IMetricQuality{
  name?: string;
  key: string;
}
export interface IGetQualityReport extends IGetQualityReportRsp{
  metricQuality?: IMetric[];
}

/**
 * 产品成员管理
 */
export class MetricService extends AbstractHttpClient {
  constructor() {
    super({
      serviceName: TrpcService.MetricService,
    });
  }

  public async getMetrics(req: IGetMetricsReq): Promise<IMetricData[]> {
    try {
      const res = await this.trpcPost('GetMetrics', req);
      return res?.metrics ?? [];
    } catch (e) {
      return [];
    }
  }

  public async getDimensionData(req: IGetDimensionDataReq): Promise<IDimensionData | null> {
    try {
      const res = await this.trpcPost('GetDimensionData', req);
      return res?.data ?? null;
    } catch (e) {
      return null;
    }
  }

  public async getDimensionList(req: IGetDimensionListReq): Promise<IGetDimensionListRsp | null> {
    try {
      const res = await this.trpcPost('GetDimensionList', req);
      return res;
    } catch (e) {
      return null;
    }
  }

  public async getPaginationDimensionData(req: IGetDimensionDataReq): Promise<IGetDimensionDataRsp | null> {
    try {
      return await this.trpcPost('GetDimensionData', req);
    } catch (e) {
      return null;
    }
  }

  public async getTopData(req: IGetTopDataReq): Promise<IGetTopDataRsp | null> {
    try {
      return await this.trpcPost('GetTopData', req);
    } catch (e) {
      return null;
    }
  }

  /**
   * 拉取 dashboard 接口
   */
  public async loadDashboard(req: IGetDashboardDataReq): Promise<Dashboard>  {
    try {
      const rsp = await this.trpcPost<NoUndefinedField<IGetDashboardDataRsp>>('GetDashboardData', req);

      return {
        data: rsp.data?.map((item: { metricName: string; dataPoints: (string | number)[] }) => ({
          metricName: item.metricName,
          dataPoints: item.dataPoints,
        })),
        timePoints: (rsp.timePoints ?? []).map(timePoint => fromPBTime(timePoint).format()),
      };
    } catch (e) {
      logger.error('loadDashboard err: ', e);
      return {
        data: [],
        timePoints: [],
      };
    }
  };

  // 查询指定条件下的产品质量报告
  public async getQualityReport(req: IGetQualityReportReq): Promise<IGetQualityReport> {
    try {
      const rsp =  await this.trpcPost<IGetQualityReportRsp>('GetQualityReport', req);
      // 增加name
      return { ...rsp, metricQuality: rsp.metricQuality?.map(item => ({
        ...item,
        name: getName(item.type as QualityReportMetricType),
        key: uuid.v4(),
      })) };
    } catch (e) {
      logger.error('GetQualityReport err: ', e);
      return {};
    }
  }

  // 查询指定条件下的产品质量天趋势数据
  public async getQualityReportTrend(req: IGetQualityReportTrendReq): Promise<IGetQualityReportTrendRsp> {
    try {
      return await this.trpcPost('GetQualityReportTrend', req);
    } catch (e) {
      logger.error('GetQualityReportTrend err: ', e);
      return {};
    }
  }

  // 指标数据概览
  public async getSummaryData(req: IGetSummaryDataReq): Promise<IGetSummaryDataRsp> {
    try {
      return await this.trpcPost('GetSummaryData', req);
    } catch (e) {
      logger.error('getSummaryData err: ', e);
      return {};
    }
  }

  public async getStandardSummary(req: IGetStandardSummaryReq): Promise<IGetStandardSummaryRsp> {
    try {
      return await this.trpcPost('GetStandardSummary', req);
    } catch (e) {
      logger.error('getStandardSummary err: ', e);
      return {};
    }
  }

  // 今日概览
  public async getSummary(req: any): Promise<IGetSummaryRsp> {
    try {
      return await this.trpcPost('GetSummary', req);
    } catch (e) {
      logger.error('getSummaryData err: ', e);
      return {};
    }
  }

  // 今日概览
  public async getChartDashboardData(req: IGetChartDashboardDataReq): Promise<IGetChartDashboardDataRsp> {
    try {
      return await this.trpcPost('GetChartDashboardData', req);
    } catch (e) {
      logger.error('getChartDashboardData err: ', e);
      return {};
    }
  }
}

export const metricService = new MetricService();
